import React, { useState } from 'react';
import { X, Video, Link as LinkIcon, AlertCircle } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import DateInput from '../ui/DateInput';

// PreMeetingScheduler — modal for the shooter (spec §5) to schedule a
// pre-event planning call.
//
// Two modes the user can toggle between:
//   Auto — generate Meet link via Google Calendar API (requires OAuth)
//   Manual — paste a Meet/Zoom/whatever URL the shooter generated elsewhere
//
// The endpoint accepts both via the body shape it receives. If the user
// flips to Auto and OAuth isn't connected, the server returns 409
// oauth_expired and we surface a "Reconnect Google" suggestion.

export default function PreMeetingScheduler({ event, onClose, onScheduled, oauthConnected = false }) {
  const isReschedule = !!event.premeetingMeetUrl;
  const [mode, setMode] = useState(oauthConnected ? 'auto' : 'manual');
  const [scheduledAt, setScheduledAt] = useState(
    event.premeetingScheduledAt ? isoToLocalInput(event.premeetingScheduledAt) : defaultScheduledAt(event.scheduledStartAt),
  );
  const [meetUrl, setMeetUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const body = { scheduledAt };
      if (mode === 'manual') {
        if (!meetUrl.trim()) throw new Error('Paste a Meet URL or switch to auto-generate mode.');
        body.meetUrl = meetUrl.trim();
      }
      const r = await api.post(`/api/media/me/events/${event.id}/premeeting`, body);
      onScheduled?.(r);
    } catch (err) {
      if (err instanceof ApiError && err.data?.code === 'oauth_expired') {
        setError('Your Google connection expired. Reconnect at /media/settings or switch to manual paste.');
      } else if (err instanceof ApiError && err.data?.code === 'oauth_not_configured') {
        setError('Google auto-generate is unavailable on this environment. Paste a Meet URL manually.');
      } else {
        setError(err instanceof ApiError ? err.message : (err.message || 'Failed to schedule'));
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-[#0a0a0a] border border-white/[0.08] rounded-2xl shadow-2xl">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-white/[0.06]">
          <div>
            <h2 className="text-base font-semibold text-white tracking-tight">{isReschedule ? 'Reschedule pre-event meeting' : 'Schedule pre-event meeting'}</h2>
            <p className="text-[12px] text-gray-500 mt-0.5 truncate" title={event.eventTitle}>
              {event.eventTitle}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-2 flex items-center gap-1 border-b border-white/[0.04]">
          <TabBtn active={mode === 'auto'} onClick={() => setMode('auto')} disabled={!oauthConnected}>
            <Video className="w-3.5 h-3.5" /> Auto-generate Meet
          </TabBtn>
          <TabBtn active={mode === 'manual'} onClick={() => setMode('manual')}>
            <LinkIcon className="w-3.5 h-3.5" /> Paste URL
          </TabBtn>
        </div>

        <form onSubmit={onSubmit} className="px-5 py-4 space-y-4">
          {error && (
            <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-[12px] text-red-300 flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              {error}
            </div>
          )}

          {!oauthConnected && mode === 'auto' && (
            <div className="px-3 py-2 rounded-lg border border-amber-400/30 bg-amber-500/[0.04] text-[12px] text-amber-100/90">
              Google isn't connected, auto-generate unavailable. <a href="/media/settings" className="underline">Connect here.</a>
            </div>
          )}

          <label className="block">
            <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Meeting time</span>
            <DateInput withTime required value={scheduledAt} onChange={setScheduledAt}
              className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white text-[13px]" />
            <span className="block text-[10px] text-gray-500 mt-1">Default: 7 days before the event. 30-min duration.</span>
          </label>

          {mode === 'manual' && (
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Meet / Zoom URL</span>
              <input type="url" required value={meetUrl} onChange={(e) => setMeetUrl(e.target.value)}
                placeholder="https://meet.google.com/abc-defg-hij"
                className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white text-[13px]" />
            </label>
          )}

          {mode === 'auto' && oauthConnected && (
            <div className="text-[11px] text-gray-500">
              A Google Calendar event will be created on your calendar with a Meet link and the school's primary contact invited.
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/[0.05]">
            <button type="button" onClick={onClose} className="px-4 py-2 text-[12px] text-gray-400 hover:text-white">
              Cancel
            </button>
            <button type="submit" disabled={submitting || (mode === 'auto' && !oauthConnected)}
              className="px-4 py-2 rounded-full bg-[#B0FC38] text-gray-900 text-[13px] font-medium hover:bg-[#9BE22E] disabled:opacity-50">
              {submitting ? 'Saving…' : (isReschedule ? 'Reschedule' : 'Schedule')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, disabled, children }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className={`inline-flex items-center gap-1.5 px-3 py-2 text-[12px] border-b-2 -mb-px transition-colors ${
        active ? 'border-[#B0FC38] text-white'
          : disabled ? 'border-transparent text-gray-700 cursor-not-allowed'
          : 'border-transparent text-gray-500 hover:text-gray-300'
      }`}>
      {children}
    </button>
  );
}

// Convert a stored ISO timestamp to the YYYY-MM-DDTHH:MM local format the
// DateInput expects (used to prefill when re-scheduling an existing meeting).
function isoToLocalInput(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.valueOf())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Default the pre-event meeting to 7 days before the event at 10am local
// (rounded so the picker shows a sensible value). Falls back to "now + 1h"
// if the event is sooner than 7 days out.
function defaultScheduledAt(eventStartIso) {
  const start = new Date(eventStartIso);
  if (Number.isNaN(start.valueOf())) return '';
  const sevenBefore = new Date(start.getTime() - 7 * 86_400_000);
  sevenBefore.setHours(10, 0, 0, 0);
  const target = sevenBefore.getTime() > Date.now() + 3_600_000
    ? sevenBefore
    : new Date(Date.now() + 3_600_000);
  // datetime-local needs YYYY-MM-DDTHH:MM (no seconds, no Z)
  const pad = (n) => String(n).padStart(2, '0');
  return `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}T${pad(target.getHours())}:${pad(target.getMinutes())}`;
}
